import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "@environments/environment";
import { Observable, throwError } from "rxjs";
import { Agreement } from "@models/agreement.model";
import { catchError } from "rxjs/operators";
import { UploadDocumentResponse } from "@models/upload-document-response";

@Injectable({
  providedIn: "root",
})
export class AgreementsService {
  private apiUrl = environment.apiUrl;

  getAgreements(details: any): Observable<Agreement[]> {
    let params = new HttpParams()
      .set("year", details.year)
      .set("orgId", details.orgId);

    return this.http.get<Agreement[]>(`${this.apiUrl}/Docusign/GetEnvelopes`, {
      params: params,
    });
  }

  getRecipientView(envelopeId: string): Observable<any> {
    let params = new HttpParams()
      .set("envelopeId", envelopeId);

    return this.http.get<string>(`${this.apiUrl}/Docusign/GetRecipientView`, {
      params: params,
    });
  }

  makeEnvelope(orgId: number, recipientEmail?: string | null, recipientName?: string | null): Observable<string> {
    return this.http.post<string>(
      `${this.apiUrl}/Docusign/CreateEnvelope`,
      { orgId, recipientEmail: recipientEmail ?? null, recipientName: recipientName ?? null }
    );
  }

  voidOrgAgreement(envelopeId: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/Docusign/VoidEnvelope`,
      { envelopeId }
    );
  }

  uploadDocument(file: File, organizationId: number, documentTypeId: number) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("organizationId", organizationId.toString());
    formData.append("documentTypeId", documentTypeId.toString());

    return this.http
      .post<UploadDocumentResponse>(`${this.apiUrl}/documents`, formData)
      .pipe(
        catchError((error) => {
          let errorMessage = "Upload failed";

          if (error.status === 409) {
            errorMessage =
              error.error?.detail ||
              error.error?.title ||
              "A document with this name already exists";
          } else if (error.error?.detail) {
            errorMessage = error.error.detail;
          } else if (error.error?.title) {
            errorMessage = error.error.title;
          } else if (error.message) {
            errorMessage = error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getUploadedDocuments(
    organizationId: number,
    documentTypeId?: number
  ): Observable<UploadDocumentResponse[]> {
    let params = new HttpParams().set(
      "organizationId",
      organizationId.toString()
    );

    if (documentTypeId) {
      params = params.set("documentTypeId", documentTypeId.toString());
    }

    return this.http.get<UploadDocumentResponse[]>(`${this.apiUrl}/documents`, {
      params,
    });
  }

  downloadDocument(
    documentId: number,
    organizationId: number
  ): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/documents/${documentId}/download?organizationId=${organizationId}`,
      {
        responseType: "blob",
      }
    );
  }

  downloadOrgAgreement(envelopeId: string): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/Docusign/DownloadDocument?envelopeId=${envelopeId}`,
      { responseType: "blob" }
    );
  }

  constructor(private http: HttpClient) {}
}
